import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { useParams, useNavigate } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import UsageLimitComponent from "./component";

const UsageLimitContainer = (props) => {
  const { corporateId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [usageLimitData, setUsageLimitData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    usageLimitRequired: false,
    limits: []
  });

  // Helper function to convert API frequency to UI format
  const convertApiFrequencyToUi = (frequency) => {
    if (!frequency) return "DAYS";
    const upperFreq = frequency.toUpperCase();
    if (upperFreq === "HOUR") return "HOURS";
    if (upperFreq === "DAY") return "DAYS";
    if (upperFreq === "MONTH") return "MONTHS";
    return upperFreq;
  };

  // Helper function to convert UI frequency to API format
  const convertUiFrequencyToApi = (frequency) => {
    if (!frequency) return "DAYS";
    const upperFreq = frequency.toUpperCase();
    if (upperFreq === "HOURS") return "HOURS";
    if (upperFreq === "DAYS") return "DAYS";
    if (upperFreq === "MONTHS") return "MONTHS";
    if (upperFreq === "HOUR") return "HOURS";
    if (upperFreq === "DAY") return "DAYS";
    if (upperFreq === "MONTH") return "MONTHS";
    return "DAYS";
  };

  // Map API to UI format
  const mapApiToUi = (apiData) => {
    const usageLimitRequired = apiData.usageLimitRequired === true || apiData.usageLimitRequired === "true";
    
    let limits = [];
    if (apiData.limits && Array.isArray(apiData.limits) && apiData.limits.length > 0) {
      limits = apiData.limits.map((limit) => ({
        limitOn: limit.limitOn || "AMOUNT",
        frequency: convertApiFrequencyToUi(limit.frequency),
        duration: limit.duration ? limit.duration.toString() : "1",
        value: limit.value ? limit.value.toString() : ""
      }));
    }
    
    return { usageLimitRequired, limits };
  };

  // Map UI to API format
  const mapUiToApi = (uiData) => {
    let limits = [];
    if (uiData.limits && Array.isArray(uiData.limits) && uiData.limits.length > 0) {
      limits = uiData.limits.map((limit) => ({
        limitOn: limit.limitOn || "AMOUNT",
        frequency: convertUiFrequencyToApi(limit.frequency),
        duration: parseInt(limit.duration) || 1,
        value: parseInt(limit.value) || 0
      }));
    }
    
    return {
      usageLimitRequired: uiData.usageLimitRequired === true || uiData.usageLimitRequired === "true",
      limits: limits
    };
  };

  useEffect(() => {
    if (corporateId) {
      fetchUsageLimitData();
    }
  }, [corporateId]);

  const fetchUsageLimitData = () => {
    setLoading(true);
    
    props.getDataFromAPI(
      `/partner/api/v2/corporate/${corporateId}/usage-limit`,
      "GET",
      undefined,
      (response) => {
        if (response && response.data) {
          const mappedData = mapApiToUi(response.data);
          setUsageLimitData(mappedData);
          setFormData({
            usageLimitRequired: mappedData.usageLimitRequired,
            limits: mappedData.limits || []
          });
        } else if (response && response.usageLimitRequired !== undefined) {
          const mappedData = mapApiToUi(response);
          setUsageLimitData(mappedData);
          setFormData({
            usageLimitRequired: mappedData.usageLimitRequired,
            limits: mappedData.limits || []
          });
        } else {
          const defaultData = { usageLimitRequired: false, limits: [] };
          setUsageLimitData(defaultData);
          setFormData(defaultData);
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching usage limit data:", error);
        const errorData = { usageLimitRequired: false, limits: [] };
        setUsageLimitData(errorData);
        setFormData(errorData);
        setLoading(false);
      }
    );
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelClick = () => {
    if (usageLimitData) {
      setFormData({
        usageLimitRequired: usageLimitData.usageLimitRequired,
        limits: JSON.parse(JSON.stringify(usageLimitData.limits))
      });
    }
    setIsEditMode(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateLimit = () => {
    setLoading(true);
    
    const apiPayload = mapUiToApi(formData);
    
    props.getDataFromAPI(
      `/partner/api/v2/corporate/${corporateId}/usage-limit`,
      "PUT",
      apiPayload,
      (response) => {
        const updatedData = {
          usageLimitRequired: formData.usageLimitRequired,
          limits: JSON.parse(JSON.stringify(formData.limits))
        };
        
        setUsageLimitData(updatedData);
        toastr.success("Success", "Usage limit updated successfully");
        setLoading(false);
        setIsEditMode(false);
        fetchUsageLimitData();
      },
      (error) => {
        console.error("PUT API Error:", error);
        let errorMessage = "Unable to update usage limit";
        if (error.response && error.response.data && error.response.data.status && error.response.data.status.message) {
          errorMessage = error.response.data.status.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        toastr.error("Failed", errorMessage);
        setLoading(false);
      }
    );
  };

  return (
    <UsageLimitComponent
      loading={loading}
      usageLimitData={usageLimitData}
      isEditMode={isEditMode}
      formData={formData}
      handleBackClick={handleBackClick}
      handleEditClick={handleEditClick}
      handleCancelClick={handleCancelClick}
      handleInputChange={handleInputChange}
      handleUpdateLimit={handleUpdateLimit}
      corporateId={corporateId}
    />
  );
};

export default connect(null, { getDataFromAPI })(UsageLimitContainer);